import { __assign, __makeTemplateObject, __rest } from "tslib";
import React, { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef, } from 'react';
import { combineProps, getSelectionPosition, setSelectionPosition, styled, useEventCallback, useForkRef, useKeyDownOnce, useThemeProps, } from '../../../foundation';
import { RcTextField } from '../../Forms/TextField';
import { ACCEPTABLE_KEYS } from '../DialPad/utils';
import { useRcDialerContext } from '../utils';
import { DialTextFieldStyle } from './styles';
import { getDialPadValueOnlyRegex, useFixedEndSelection } from './utils';
var _RcDialTextField = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcDialTextField' });
    var children = props.children, value = props.value, onChange = props.onChange, onEmit = props.onEmit, onKeyPress = props.onKeyPress, onKeyDown = props.onKeyDown, onKeyUp = props.onKeyUp, inputRefProp = props.inputRef, inputPropsProp = props.inputProps, InputPropsProp = props.InputProps, onlyAllowKeypadValue = props.onlyAllowKeypadValue, keypadMode = props.keypadMode, _a = props.externalWindow, externalWindow = _a === void 0 ? window : _a, rest = __rest(props, ["children", "value", "onChange", "onEmit", "onKeyPress", "onKeyDown", "onKeyUp", "inputRef", "inputProps", "InputProps", "onlyAllowKeypadValue", "keypadMode", "externalWindow"]);
    var dialerContext = useRcDialerContext();
    var _b = useFixedEndSelection(), inputRef = _b.ref, onKeypadModeFocus = _b.onFocus, onKeypadModeKeyDown = _b.onKeyDown, onKeypadModeClick = _b.onClick;
    var forkContextRef = useForkRef(dialerContext.inputRef, inputRef);
    var forkInputRef = useForkRef(inputRefProp, forkContextRef);
    var scrollToPositionRef = useRef();
    var isNotTriggerRefocusRef = useRef(false);
    var maxLength = keypadMode ? undefined : inputPropsProp === null || inputPropsProp === void 0 ? void 0 : inputPropsProp.maxLength;
    var checkNotReFocus = useEventCallback(function (reason, isFocus) {
        var elm = inputRef.current;
        var isInputFocus = isFocus !== null && isFocus !== void 0 ? isFocus : externalWindow.document.activeElement === elm;
        if (reason !== 'click' && !isInputFocus) {
            // ! both keydown and touch, only when focus need re-focus
            isNotTriggerRefocusRef.current = true;
        }
    });
    /**
     * outside clear via this method, with check current input state
     */
    useImperativeHandle(dialerContext.onClearRef, function () { return function (e, reason) {
        // * when clear from keyboard,
        // * must need re-focus, so not check,
        if (reason !== 'keyboard') {
            checkNotReFocus(reason);
        }
        scrollToPositionRef.current = 0;
        onChange === null || onChange === void 0 ? void 0 : onChange('');
    }; }, [checkNotReFocus, onChange]);
    /**
     * outside delete value via this method, with check current input state
     */
    useImperativeHandle(dialerContext.onDeleteRef, function () { return function (e, reason) {
        var elm = inputRef.current;
        if (elm && (value === null || value === void 0 ? void 0 : value.length)) {
            var isFocus = externalWindow.document.activeElement === elm;
            var toPosition = void 0;
            var toValue = void 0;
            if (isFocus) {
                var _a = getSelectionPosition(inputRef), position = _a.position, isSelectRange = _a.isSelectRange;
                if (isSelectRange) {
                    toPosition = position.start;
                    toValue =
                        value.slice(0, position.start) + value.slice(position.end);
                }
                else if (position.start > 0) {
                    toPosition = position.start - 1;
                    toValue = value.slice(0, toPosition) + value.slice(position.end);
                }
            }
            else {
                toPosition = value.length - 1;
                toValue = value.slice(0, -1);
            }
            if (toValue !== undefined) {
                // * when delete from keyboard and value to be empty,
                // * must need re-focus, so not check,
                if (!(reason === 'keyboard' && toValue === '')) {
                    checkNotReFocus(reason);
                }
                scrollToPositionRef.current = toPosition;
                onChange === null || onChange === void 0 ? void 0 : onChange(toValue);
            }
        }
    }; }, [checkNotReFocus, externalWindow, inputRef, onChange, value]);
    /**
     * outside change value via this method, with check current input state
     */
    useImperativeHandle(dialerContext.onInsertRef, function () { return function (addValue, reason) {
        var elm = inputRef.current;
        if (elm) {
            var isFocus = externalWindow.document.activeElement === elm;
            var toPosition = void 0;
            var toValue = void 0;
            // * when keypadMode also add value at latest
            if (!isFocus || keypadMode) {
                toValue = value + addValue;
                toPosition = toValue.length;
            }
            else {
                var position = getSelectionPosition(inputRef).position;
                toValue =
                    value.slice(0, position.start) +
                        addValue +
                        value.slice(position.end);
                toPosition = position.start + 1;
            }
            if (maxLength === undefined ||
                (maxLength !== undefined && toValue.length <= maxLength)) {
                checkNotReFocus(reason, isFocus);
                scrollToPositionRef.current = toPosition;
                onChange === null || onChange === void 0 ? void 0 : onChange(toValue);
                onEmit === null || onEmit === void 0 ? void 0 : onEmit(addValue, reason);
            }
        }
    }; }, [
        checkNotReFocus,
        externalWindow,
        inputRef,
        keypadMode,
        maxLength,
        onChange,
        onEmit,
        value,
    ]);
    useLayoutEffect(function () {
        var scrollToPosition = scrollToPositionRef.current;
        if (!isNotTriggerRefocusRef.current && scrollToPosition !== undefined) {
            setSelectionPosition(inputRef, {
                start: scrollToPosition,
                end: scrollToPosition,
                scrollIntoView: true,
            });
        }
        scrollToPositionRef.current = undefined;
        isNotTriggerRefocusRef.current = false;
    });
    var events = useKeyDownOnce(ACCEPTABLE_KEYS, {
        onKeyDown: function (e) {
            var _a, _b;
            (_b = (_a = dialerContext.dialPadActionRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.playAudio(e.key);
            if (keypadMode) {
                if (['Delete', 'Backspace'].includes(e.key)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                onKeypadModeKeyDown(e);
            }
            onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        },
        onKeyUp: onKeyUp,
    });
    var handleChange = useEventCallback(function (e) {
        var currValue = e.target.value;
        // * handle copy paste
        if (onlyAllowKeypadValue) {
            var filterValue = (currValue.match(getDialPadValueOnlyRegex()) || []).join('');
            if (filterValue === value)
                return;
            var newValue = filterValue.slice(-1);
            if (newValue !== '') {
                onEmit === null || onEmit === void 0 ? void 0 : onEmit(newValue, 'keyboard');
            }
            onChange(filterValue);
            return;
        }
        onChange(currValue);
    });
    var handleKeyPress = useEventCallback(function (e) {
        onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(e);
        if (onlyAllowKeypadValue) {
            var isMatch = getDialPadValueOnlyRegex().test(e.key);
            if (!isMatch) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    });
    var InputProps = useMemo(function () {
        return combineProps({
            onFocus: keypadMode ? onKeypadModeFocus : undefined,
            onClick: keypadMode ? onKeypadModeClick : undefined,
            onKeyPress: handleKeyPress,
            onChange: handleChange,
            onKeyDown: events.onKeyDown,
            onKeyUp: events.onKeyUp,
        }, InputPropsProp);
    }, [
        InputPropsProp,
        events.onKeyDown,
        events.onKeyUp,
        handleChange,
        handleKeyPress,
        keypadMode,
        onKeypadModeClick,
        onKeypadModeFocus,
    ]);
    var inputProps = useMemo(function () { return (__assign(__assign({}, inputPropsProp), { maxLength: maxLength })); }, [inputPropsProp, maxLength]);
    return (React.createElement(RcTextField, __assign({ ref: ref, inputRef: forkInputRef, clearBtn: false, value: value, variant: "borderLess", inputProps: inputProps, InputProps: InputProps, autoComplete: "off" }, rest), children));
});
var RcDialTextField = styled(_RcDialTextField)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), DialTextFieldStyle);
RcDialTextField.defaultProps = {
    inputProps: {
        maxLength: 30,
    },
    textVariant: 'headline1',
};
RcDialTextField.displayName = 'RcDialTextField';
export { RcDialTextField };
var templateObject_1;
